// src/types/api-errors.ts
//
// Canonical error contract for the web FE. AB#1919 (WS-5b) — the backend now
// emits RFC 7807 Problem Details fields (`type`/`title`/`status`/`detail`/
// `instance`) ADDITIVELY on every error body, alongside the pre-existing legacy
// fields. This file is the single source of truth for reading an error body:
//
//   - `AppError` is the canonical internal shape every consumer reads.
//   - `normalizeApiError()` is the ONE place that touches a raw error body.
//     It PREFERS the 7807 fields and FALLS BACK to legacy, so it works against
//     both the current backend (which still emits the legacy fields too) and a
//     future backend where WS-5c has removed them.
//
// The #1 correctness detail (arch-brief AB#1919 §3): the backend has TWO error
// emitters with DIFFERENT code-key names — `ExceptionMiddleware` uses
// `errorCode`, `ResultExtensions.ErrorBody` uses `code`. The normalizer reads
// `errorCode ?? code` so converted-handler errors keep their i18n key.

/**
 * HTTP status codes the FE has historically narrowed on.
 * @deprecated WS-5c (AB#1921) — prefer reading {@link AppError.status} (a plain
 *   `number` sourced from the 7807 `status` member). This narrowed union exists
 *   only for the legacy {@link ErrorResponse.statusCode} field, which the
 *   backend still emits but will remove in WS-5c.
 */
export type ErrorCode = 400 | 401 | 403 | 404 | 500;

/**
 * The LEGACY backend error body shape (pre-RFC-7807).
 *
 * @deprecated WS-5c (AB#1921) will REMOVE the `statusCode` and `message` members
 *   from the backend error envelope. Do NOT read these directly — read a
 *   {@link AppError} produced by {@link normalizeApiError} instead, which maps
 *   `statusCode → status` and `message → detail` with a 7807-preferred fallback.
 *   The non-deprecated members (`code`, `correlationId`, `errors`) are unchanged
 *   by WS-5b/WS-5c.
 */
export interface ErrorResponse {
    /**
     * @deprecated WS-5c — read {@link AppError.detail} (sourced from the 7807
     *   `detail` member) instead. The backend still emits `message` today.
     */
    message?: string;
    /**
     * @deprecated WS-5c — read {@link AppError.status} (sourced from the 7807
     *   `status` member) instead. The backend still emits `statusCode` today.
     */
    statusCode?: ErrorCode;
    /** Field-level validation errors. Unchanged by WS-5b/WS-5c. */
    errors?: Record<string, string[]>;
    /**
     * Stable DOMAIN.CODE used for the `errors:<CODE>` i18n lookup. Emitted by the
     * `ResultExtensions.ErrorBody` (converted-handler) path. Unchanged by WS-5b.
     */
    code?: string;
    /**
     * Stable DOMAIN.CODE emitted by the `ExceptionMiddleware` (throw) path.
     * Same semantics as {@link ErrorResponse.code}; the divergent key name is
     * reconciled by {@link normalizeApiError} (`errorCode ?? code`). Unchanged
     * by WS-5b; converges with `code` in WS-5c.
     */
    errorCode?: string;
    /** Correlation id for support/log lookups. Unchanged by WS-5b/WS-5c. */
    correlationId?: string;
}

/**
 * The RFC 7807 Problem Details members the backend now emits additively
 * (AB#1918 / WS-5a). Hand-written (no codegen — arch-brief §5). These are the
 * CANONICAL source fields {@link normalizeApiError} prefers.
 */
export interface ProblemDetails {
    /** `urn:devanza:error:{code}` (or a framework URI for built-in 4xx). */
    type?: string;
    /** Short, human-readable summary (stable English; i18n still keyed on code). */
    title?: string;
    /** HTTP status — the canonical replacement for the legacy `statusCode`. */
    status?: number;
    /** Human-readable detail — the canonical replacement for legacy `message`. */
    detail?: string;
    /** Request path (logging/correlation only — never rendered; PDPL). */
    instance?: string;
}

/**
 * Canonical internal error object. EVERY consumer reads THIS — never the raw
 * axios/fetch body. Produced only by {@link normalizeApiError}.
 */
export interface AppError {
    /** i18n key for `errors:<CODE>`. `raw.errorCode ?? raw.code` (§3). */
    code: string;
    /** HTTP status. `raw.status ?? raw.statusCode` (7807-preferred). */
    status: number;
    /** Human-readable detail. `raw.detail ?? raw.message` (7807-preferred). */
    detail: string;
    /** 7807 title (stable English fallback). */
    title?: string;
    /** 7807 type URI (`urn:devanza:error:{code}`). */
    type?: string;
    /** Correlation id (unchanged by WS-5b — keep reading). */
    correlationId?: string;
    /** 7807 instance (request path) — logging/correlation only, never rendered. */
    instance?: string;
    /** Field-level validation errors (unchanged by WS-5b). */
    errors?: Record<string, string[]>;
}

/** Sentinel code when no code/errorCode is present — maps to `errors:GENERIC`. */
export const UNKNOWN_ERROR_CODE = 'UNKNOWN';

/**
 * Pull a candidate raw error body out of whatever a consumer hands us: an axios
 * error (`err.response.data`), an already-rejected `{ data }`, a bare body, or
 * an `Error`. Returns a plain record we can read 7807/legacy keys off of.
 */
function unwrapRawBody(input: unknown): Record<string, unknown> {
    if (input == null || typeof input !== 'object') {
        return {};
    }
    const obj = input as Record<string, unknown>;

    // axios error: the body lives at err.response.data
    const response = obj.response as
        | { data?: unknown; status?: unknown; headers?: unknown }
        | undefined;
    if (response && typeof response === 'object' && response.data && typeof response.data === 'object') {
        const data = { ...(response.data as Record<string, unknown>) };
        // status can live on response (HTTP layer) when the body omits it.
        if (data.status == null && data.statusCode == null && typeof response.status === 'number') {
            data.status = response.status;
        }
        // x-correlation-id header is a last-resort correlation source.
        if (data.correlationId == null && response.headers && typeof response.headers === 'object') {
            const headers = response.headers as Record<string, unknown>;
            const headerCid = headers['x-correlation-id'] ?? headers['X-Correlation-Id'];
            if (typeof headerCid === 'string') {
                data.correlationId = headerCid;
            }
        }
        return data;
    }

    // already a raw body (e.g. queryClient handed us the rejected value, or a
    // { data } envelope). Prefer a nested `data` object if present.
    if (obj.data && typeof obj.data === 'object') {
        return obj.data as Record<string, unknown>;
    }

    return obj;
}

/**
 * THE normalizer. Produces a canonical {@link AppError} from any raw error
 * value (axios error, fetch body, rejected promise value, or `Error`).
 *
 * Resolution rules (arch-brief AB#1919 §4 — PREFER 7807, FALL BACK to legacy):
 *   - `code   = raw.errorCode ?? raw.code`   (§3 — the load-bearing line)
 *   - `status = raw.status ?? raw.statusCode`
 *   - `detail = raw.detail ?? raw.message`
 *
 * Defensive by design: handles converted handlers (`code` + 7807), un-converted
 * handlers (`errorCode` + 7807), and non-middleware bodies (proxy/gateway 502
 * with neither 7807 nor a Devanza code — legacy `statusCode`/`message` only).
 */
export function normalizeApiError(raw: unknown): AppError {
    const r = unwrapRawBody(raw);

    const code = (r.errorCode ?? r.code) as string | undefined;
    const status = (r.status ?? r.statusCode) as number | undefined;
    const detail = (r.detail ?? r.message) as string | undefined;

    return {
        code: code && code.length > 0 ? code : UNKNOWN_ERROR_CODE,
        status: typeof status === 'number' ? status : 0,
        detail: typeof detail === 'string' ? detail : '',
        title: typeof r.title === 'string' ? r.title : undefined,
        type: typeof r.type === 'string' ? r.type : undefined,
        correlationId: typeof r.correlationId === 'string' ? r.correlationId : undefined,
        instance: typeof r.instance === 'string' ? r.instance : undefined,
        errors:
            r.errors && typeof r.errors === 'object'
                ? (r.errors as Record<string, string[]>)
                : undefined,
    };
}

/**
 * Convenience reader for the common "show the server's human message, else a
 * local fallback" idiom found across the hooks/components. Routes through
 * {@link normalizeApiError} so the message is sourced from the RFC 7807 `detail`
 * member (falling back to the legacy `message`) — never a raw body read. This
 * is the AB#1919 (WS-5b) replacement for the repo-wide
 * `(err as {...}).response?.data?.message || 'fallback'` pattern, and it makes
 * the WS-5c legacy-field removal a single-file change.
 *
 * @param err      the raw caught error (axios error, fetch body, or `Error`)
 * @param fallback the local default shown when the server provided no `detail`
 */
export function getApiErrorMessage(err: unknown, fallback: string): string {
    const { detail } = normalizeApiError(err);
    if (detail) {
        return detail;
    }
    // Last resort: a bare `Error` (e.g. a thrown `new Error(msg)` from a hook
    // that already unwrapped, or a non-HTTP failure) — surface its `.message`.
    if (err instanceof Error && err.message) {
        return err.message;
    }
    return fallback;
}
